import { isMultipleOfTwenty } from '@kijiji/number/isMultipleOfTwenty'
import { type PropsWithChildren, useRef } from 'react'
import { InView } from 'react-intersection-observer'

import { handleScrollingAdRefresh } from '@/features/advertisement/utils/handleScrollingAdRefresh'

export type SearchListIntersectionObserverProps = PropsWithChildren<{
  index: number
}>

/**
 * A wrapper component that adds intersection observer functionality to listing cards in a search results list.
 * Currently targets every 20th listing card to trigger advertisement refreshes when they come into view.
 * Other in view events can be added based on the listing index as needed.
 *
 * @param props - Component props
 * @param props.index - The zero-based index of the listing card in the list
 * @param props.children - The child elements to be wrapped (typically a listing card)
 * @returns A list item element with optional intersection observer functionality
 *
 * @example
 * <SearchListIntersectionObserver index={19}>
 *   <ListingCard {...cardProps} />
 * </SearchListIntersectionObserver>
 */
export const SearchListIntersectionObserver = ({
  index,
  children,
  ...props
}: SearchListIntersectionObserverProps) => {
  const recentlyRefreshed = useRef(false)

  /** It should trigger ad refreshes if listing index is a multiple of 20 */
  const indexIsMultipleOfTwenty = isMultipleOfTwenty(index + 1)

  return indexIsMultipleOfTwenty ? (
    <InView onChange={(inView) => handleScrollingAdRefresh(inView, recentlyRefreshed)}>
      {({ ref }) => (
        <li ref={ref} {...props} style={{ listStyleType: 'none' }}>
          {children}
        </li>
      )}
    </InView>
  ) : (
    <li {...props} style={{ listStyleType: 'none' }}>
      {children}
    </li>
  )
}
